import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ChevronUp, GripVertical, Trash2 } from "lucide-react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../../hooks/use-admin-form-editor-paths";
import type { BuilderNode } from "../../../types";
import { useFormBuilderCanvas } from "../form-builder-canvas-context";
import { BUILDER_ROW_ACTIONS_VISIBILITY } from "../form-builder-layout-classes";
import { translatePaletteFieldTypeLabel } from "../form-builder-palette-label";
import { BUILDER_CONTROL_FOCUS } from "../form-builder-shared-classes";

export function FormBuilderFieldRow({
	id,
	node: nodeProp,
	selectedId,
	onSelect,
}: {
	id: string;
	node?: BuilderNode;
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const { nodes, rootIds, deleteNode, moveField } = useFormBuilderCanvas();
	const node = nodeProp ?? nodes[id];
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	if (!node || node.kind !== "field") return null;

	const { field } = node;
	const isSelected = selectedId === id;

	const siblingIds =
		node.parentId === "root"
			? rootIds
			: (() => {
					const parent = nodes[node.parentId];
					return parent?.kind === "section" ? parent.childFieldIds : [];
				})();
	const fieldIndex = siblingIds.indexOf(id);
	const canMoveUp = fieldIndex > 0;
	const canMoveDown = fieldIndex >= 0 && fieldIndex < siblingIds.length - 1;
	const typeLabel = translatePaletteFieldTypeLabel(t, paths, field.fieldType);

	return (
		<li
			ref={setNodeRef}
			style={style}
			className={cx(
				"group flex cursor-pointer list-none items-center gap-2 rounded-lg border p-3",
				isSelected
					? "border-brand-500 bg-brand-50"
					: "border-border-secondary bg-secondary hover:bg-primary_hover",
			)}
			aria-label={t(paths.formEditor.a11y.fieldRowSummary, {
				label: field.label,
				type: String(typeLabel),
			})}
		>
			<button
				type="button"
				className={cx(
					"cursor-pointer text-fg-quaternary hover:text-fg-secondary",
					BUILDER_CONTROL_FOCUS,
				)}
				aria-label={t(paths.formEditor.a11y.dragHandleField, {
					name: field.label,
				})}
				{...attributes}
				{...listeners}
			>
				<GripVertical className="size-4" aria-hidden />
			</button>
			<button
				type="button"
				className={cx(
					"min-w-0 flex-1 cursor-pointer text-left",
					BUILDER_CONTROL_FOCUS,
				)}
				onClick={() => {
					onSelect(id);
				}}
				aria-pressed={isSelected}
				aria-label={t(paths.formEditor.a11y.editFieldProperties, {
					name: field.label,
				})}
			>
				<div className="font-semibold text-text-primary">
					{field.label}
					{field.required ? (
						<span className="text-error-600" aria-hidden>
							{" "}
							*
						</span>
					) : null}
				</div>
				<div className="text-sm text-text-tertiary">{String(typeLabel)}</div>
			</button>
			<div
				className={cx("flex shrink-0 gap-1", BUILDER_ROW_ACTIONS_VISIBILITY)}
			>
				<button
					type="button"
					disabled={!canMoveUp}
					className={cx(
						"rounded p-1 text-text-tertiary",
						BUILDER_CONTROL_FOCUS,
						canMoveUp
							? "cursor-pointer hover:bg-background-tertiary"
							: "cursor-not-allowed opacity-40",
					)}
					onClick={(e) => {
						e.stopPropagation();
						if (!canMoveUp) return;
						moveField(id, "up");
					}}
					aria-label={t(paths.formEditor.builder.moveUp)}
				>
					<ChevronUp className="size-4" aria-hidden />
				</button>
				<button
					type="button"
					disabled={!canMoveDown}
					className={cx(
						"rounded p-1 text-text-tertiary",
						BUILDER_CONTROL_FOCUS,
						canMoveDown
							? "cursor-pointer hover:bg-background-tertiary"
							: "cursor-not-allowed opacity-40",
					)}
					onClick={(e) => {
						e.stopPropagation();
						if (!canMoveDown) return;
						moveField(id, "down");
					}}
					aria-label={t(paths.formEditor.builder.moveDown)}
				>
					<ArrowDown className="size-4" aria-hidden />
				</button>
				<button
					type="button"
					className={cx(
						"cursor-pointer rounded p-1 text-text-tertiary transition-colors",
						BUILDER_CONTROL_FOCUS,
						"hover:bg-error-50 hover:text-error-600",
					)}
					aria-label={t(paths.formEditor.builder.deleteFieldNamed, {
						name: field.label,
					})}
					onClick={(e) => {
						e.stopPropagation();
						deleteNode(id);
					}}
				>
					<Trash2 className="size-4" aria-hidden />
				</button>
			</div>
		</li>
	);
}
